import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  X,
  Eye,
  EyeOff,
} from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/admin/inspections")({
  component: AdminInspectionsPage,
});

interface InspectionTemplate {
  id: string;
  name: string;
  description: string | null;
  category: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface TemplateItem {
  id: string;
  templateId: string;
  label: string;
  description: string | null;
  sortOrder: number;
  isRequired: boolean;
  createdAt: string;
}

function AdminInspectionsPage() {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "inspection-templates"],
    queryFn: () =>
      apiGet<{ data: InspectionTemplate[] }>("/api/admin/inspection-templates"),
  });

  const templates = data?.data || [];
  const selectedTemplate = templates.find((t) => t.id === selectedId);

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiDelete(`/api/admin/inspection-templates/${id}`),
    onSuccess: () => {
      toast.success("Template deactivated");
      queryClient.invalidateQueries({
        queryKey: ["admin", "inspection-templates"],
      });
      if (selectedId) setSelectedId(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (isLoading) {
    return (
      <div className="space-y-2 py-8">
        <div className="h-4 bg-muted rounded animate-pulse" />
        <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Template list */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Inspection Templates
            </h2>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowCreate(!showCreate)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>

          {showCreate && (
            <CreateTemplateForm onClose={() => setShowCreate(false)} />
          )}

          {templates.length === 0 && !showCreate && (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No inspection templates yet.
            </p>
          )}

          {templates.map((t) =>
            editingId === t.id ? (
              <EditTemplateForm
                key={t.id}
                template={t}
                onClose={() => setEditingId(null)}
              />
            ) : (
              <Card
                key={t.id}
                className={cn(
                  "cursor-pointer transition-colors",
                  selectedId === t.id && "ring-2 ring-primary"
                )}
                onClick={() =>
                  setSelectedId(selectedId === t.id ? null : t.id)
                }
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm truncate">
                          {t.name}
                        </span>
                        <Badge variant="outline" className="text-xs px-1.5 py-0">
                          {t.category}
                        </Badge>
                        {!t.isActive && (
                          <Badge variant="secondary" className="text-xs px-1.5 py-0">
                            Inactive
                          </Badge>
                        )}
                      </div>
                      {t.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {t.description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingId(t.id);
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm("Deactivate this template?")) {
                            deleteMutation.mutate(t.id);
                          }
                        }}
                      >
                        {t.isActive ? (
                          <Eye className="h-3.5 w-3.5" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          )}
        </div>

        {/* Items panel */}
        <div>
          {selectedTemplate ? (
            <TemplateItemsPanel template={selectedTemplate} />
          ) : (
            <div className="flex items-center justify-center h-48 border border-dashed rounded-lg">
              <p className="text-sm text-muted-foreground">
                Select a template to view items
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CreateTemplateForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("site");

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPost("/api/admin/inspection-templates", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "inspection-templates"] });
      toast.success("Template created");
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">New Template</span>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 text-sm" />
          </div>
          <div>
            <Label className="text-xs">Category</Label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
            >
              <option value="site">Site</option>
              <option value="equipment">Equipment</option>
              <option value="vehicle">Vehicle</option>
              <option value="ppe">PPE</option>
            </select>
          </div>
        </div>
        <div>
          <Label className="text-xs">Description</Label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            disabled={!name.trim() || mutation.isPending}
            onClick={() => mutation.mutate({ name: name.trim(), description: description.trim() || undefined, category })}
          >
            {mutation.isPending ? "Creating..." : "Create"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EditTemplateForm({ template, onClose }: { template: InspectionTemplate; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description || "");
  const [category, setCategory] = useState(template.category);

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPatch(`/api/admin/inspection-templates/${template.id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "inspection-templates"] });
      toast.success("Template updated");
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Edit Template</span>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 text-sm" />
          </div>
          <div>
            <Label className="text-xs">Category</Label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
            >
              <option value="site">Site</option>
              <option value="equipment">Equipment</option>
              <option value="vehicle">Vehicle</option>
              <option value="ppe">PPE</option>
            </select>
          </div>
        </div>
        <div>
          <Label className="text-xs">Description</Label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            disabled={!name.trim() || mutation.isPending}
            onClick={() => mutation.mutate({ name: name.trim(), description: description.trim() || null, category })}
          >
            {mutation.isPending ? "Saving..." : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function TemplateItemsPanel({ template }: { template: InspectionTemplate }) {
  const queryClient = useQueryClient();
  const [showAddItem, setShowAddItem] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newRequired, setNewRequired] = useState(true);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "inspection-templates", template.id],
    queryFn: () =>
      apiGet<{ data: { items: TemplateItem[] } }>(
        `/api/admin/inspection-templates/${template.id}`
      ),
  });

  const items: TemplateItem[] = (data?.data as any)?.items || [];

  const updateMutation = useMutation({
    mutationFn: (newItems: Array<{ label: string; description?: string; sortOrder: number; isRequired: boolean }>) =>
      apiPatch(`/api/admin/inspection-templates/${template.id}`, {
        items: newItems,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "inspection-templates", template.id] });
      queryClient.invalidateQueries({ queryKey: ["admin", "inspection-templates"] });
      toast.success("Items updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleAddItem = () => {
    if (!newLabel.trim()) return;
    const currentItems = items.map((i) => ({
      label: i.label,
      description: i.description || undefined,
      sortOrder: i.sortOrder,
      isRequired: i.isRequired,
    }));
    updateMutation.mutate([
      ...currentItems,
      {
        label: newLabel.trim(),
        description: newDescription.trim() || undefined,
        sortOrder: currentItems.length,
        isRequired: newRequired,
      },
    ]);
    setNewLabel("");
    setNewDescription("");
    setShowAddItem(false);
  };

  const handleDeleteItem = (index: number) => {
    const updated = items
      .filter((_, i) => i !== index)
      .map((item, i) => ({
        label: item.label,
        description: item.description || undefined,
        sortOrder: i,
        isRequired: item.isRequired,
      }));
    updateMutation.mutate(updated);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Checklist Items
          </h2>
          <p className="text-xs text-muted-foreground">{template.name}</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowAddItem(!showAddItem)}>
          <Plus className="h-4 w-4 mr-1" />
          Add Item
        </Button>
      </div>

      {showAddItem && (
        <Card>
          <CardContent className="p-3 space-y-2">
            <div>
              <Label className="text-xs">Label *</Label>
              <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} className="h-9 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Input value={newDescription} onChange={(e) => setNewDescription(e.target.value)} className="h-9 text-sm" />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={newRequired}
                onChange={(e) => setNewRequired(e.target.checked)}
                id="new-item-req"
              />
              <Label htmlFor="new-item-req" className="text-xs">Required</Label>
            </div>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setShowAddItem(false)}>Cancel</Button>
              <Button size="sm" disabled={!newLabel.trim()} onClick={handleAddItem}>Add</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="h-4 bg-muted rounded animate-pulse" />
      ) : items.length === 0 ? (
        <div className="flex items-center justify-center h-32 border border-dashed rounded-lg">
          <p className="text-sm text-muted-foreground">No items yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item, idx) => (
            <Card key={item.id}>
              <CardContent className="p-3 flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate">{item.label}</span>
                    {item.isRequired && (
                      <Badge variant="secondary" className="text-xs px-1.5 py-0 bg-blue-50 text-blue-700">
                        Required
                      </Badge>
                    )}
                  </div>
                  {item.description && (
                    <p className="text-xs text-muted-foreground truncate">{item.description}</p>
                  )}
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-destructive hover:text-destructive shrink-0"
                  onClick={() => {
                    if (window.confirm("Remove this item?")) handleDeleteItem(idx);
                  }}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
